// Retention schedule — kept intentionally short. Nic runs bank-draft policies
// with a 2-year rate guarantee, so most of the "will they cancel" stress is
// handled by the carrier + terms. What Nic actually needs is:
//   1) A calendar dot on the effective date so it never slips his mind.
//   2) A 30-day check-in call so clients feel supported, not sold-and-ghosted.
//   3) A birthday touchpoint each year — cheap, warm, keeps him top-of-mind
//      and often surfaces life-event coverage changes.
//
// Design rules:
//   1) Every reminder note starts with a stable MARKER prefix (e.g. `[R0]`,
//      `[R+30]`, `[BDAY]`) so re-saving the Sold modal can delete-then-recreate
//      the auto batch without touching manual reminders.
//   2) Reminders use the existing `call` / `appt` / `task` kinds so they
//      render the same as manual reminders on the Today calendar.
//   3) Everything defaults to 10 AM local (birthdays default to 9 AM so
//      they're the first thing seen that morning).

// Anything whose `note` starts with `[R...]`, `[BDAY]`, or `[ACA-PAY]` is an
// auto-generated retention/birthday/ACA-premium reminder. Used to clean up
// before writing a new batch.
export const RETENTION_MARKER_RE = /^(\[R[+-]?\d+\]|\[BDAY\]|\[ACA-PAY\])/

// Parse YYYY-MM-DD as LOCAL date (not UTC) so a policy effective July 15
// doesn't slip to July 14 for Pacific-tz agents. new Date('2026-07-15') is UTC
// midnight; we build the local Date instead.
function parseLocalYMD(ymd, hour = 10, minute = 0) {
  const [y, m, d] = String(ymd || '').slice(0, 10).split('-').map(n => parseInt(n, 10))
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d, hour, minute, 0, 0)
}

// Build the retention reminders from an effective date. Nic's ask is
// deliberately minimal: mark the effective date so nothing falls through
// the cracks, and put a 30-day check-in on the calendar.
export function buildRetentionSchedule(effectiveDate) {
  const eff = parseLocalYMD(effectiveDate)
  if (!eff) return []

  const at = (offsetDays) => {
    const t = new Date(eff)
    t.setDate(t.getDate() + offsetDays)
    return t.toISOString()
  }

  return [
    {
      key: 'live',
      marker: '[R0]',
      offsetDays: 0,
      kind: 'appt',
      note: '[R0] Policy effective today — check in / confirm client received welcome email',
      due_at: at(0),
      enabled: true,
      why: 'So the effective date is never forgotten.',
    },
    {
      key: 'checkin30',
      marker: '[R+30]',
      offsetDays: 30,
      kind: 'call',
      note: '[R+30] 30-day check-in call — how\'s it going, any providers or questions?',
      due_at: at(30),
      enabled: true,
      why: 'Show up after the sale — advisor, not seller.',
    },
  ]
}

// Birthday reminders — one per year for the next `years` years, fired at
// 9 AM local on the client's birthday. Skips the current year if the birthday
// already passed (no point in a reminder pointing at yesterday). Cheap to
// pre-schedule 5 years since reminders are just DB rows.
export function buildBirthdaySchedule(dob, { years = 5 } = {}) {
  // DOB can be YYYY-MM-DD or an ISO datetime; take the date portion.
  const dobDate = parseLocalYMD(dob, 9, 0)
  if (!dobDate) return []

  const now = new Date()
  const currentYear = now.getFullYear()
  const out = []

  // Start from this year if the birthday hasn't happened yet; otherwise skip
  // to next year. Fire at 9 AM local so it's the first thing Nic sees.
  const thisYearBday = new Date(currentYear, dobDate.getMonth(), dobDate.getDate(), 9, 0, 0, 0)
  let firstYear = currentYear
  if (thisYearBday.getTime() < now.getTime()) firstYear = currentYear + 1

  for (let i = 0; i < years; i++) {
    const y = firstYear + i
    const t = new Date(y, dobDate.getMonth(), dobDate.getDate(), 9, 0, 0, 0)
    // Skip invalid dates (Feb 29 in non-leap years rolls to March 1 naturally,
    // so this is mostly a defensive check).
    if (isNaN(t.getTime())) continue
    const age = y - dobDate.getFullYear()
    out.push({
      key: `bday-${y}`,
      marker: '[BDAY]',
      kind: 'task',
      note: `[BDAY] Birthday text — ${age ? `turning ${age}` : 'happy birthday'}`,
      due_at: t.toISOString(),
      enabled: true,
      year: y,
      age,
      why: `Warm touchpoint. Often surfaces life changes (job, marriage, baby) that affect coverage.`,
    })
  }
  return out
}

// ACA / government-plan monthly premium reminders. These policies bill on
// the 1st of each month, so Nic wants a reminder ON THE 1ST of every month
// following the effective date. Default 12 months of reminders — enough for
// one full policy year without stuffing the calendar.
//
// If effective date is May 15 → first reminder is June 1, then July 1, etc.
// If effective date IS the 1st (e.g. June 1) → first reminder is July 1
// (skip the effective month since the retention system already reminds on
// the effective date itself).
export function buildACAPaymentSchedule(effectiveDate, { months = 12 } = {}) {
  const eff = parseLocalYMD(effectiveDate, 10, 0)
  if (!eff) return []
  const out = []
  // Anchor at the 1st of the month AFTER the effective month
  let year = eff.getFullYear()
  let month = eff.getMonth() + 1  // month AFTER effective
  if (month > 11) { month = 0; year += 1 }
  for (let i = 0; i < months; i++) {
    const t = new Date(year, month, 1, 10, 0, 0, 0)
    out.push({
      key: `aca-${year}-${month + 1}`,
      marker: '[ACA-PAY]',
      offsetDays: null,
      kind: 'task',
      note: `[ACA-PAY] Payment reminder — ACA premium due today (month ${i + 1})`,
      due_at: t.toISOString(),
      enabled: true,
      why: 'ACA/government plans bill on the 1st. Text or call to confirm the payment cleared.',
    })
    // Advance one month
    month += 1
    if (month > 11) { month = 0; year += 1 }
  }
  return out
}

// Given the full reminders list from context, return the ones this lead
// already has that were auto-generated by a previous save. Used to delete
// stale ones before writing a fresh batch.
export function findExistingRetentionReminders(allReminders, leadId) {
  if (!Array.isArray(allReminders) || !leadId) return []
  return allReminders.filter(r =>
    r &&
    r.lead_id === leadId &&
    typeof r.note === 'string' &&
    RETENTION_MARKER_RE.test(r.note)
  )
}
